import { useCallback, useEffect, useId, useRef } from 'react';
import { register, releaseFocus, requestFocus } from '../lib/videoAudioFocus';

// Ritorna un ref-callback da assegnare al <video>. Registra il player nel coordinatore e,
// quando il video diventa udibile (!muted && volume>0 && !paused), ne chiede il focus audio
// (mettendo in pausa+muto l'eventuale video attivo precedente). Deregistra alla unmount.
export function useVideoAudioFocus(yieldFn: () => void): (el: HTMLVideoElement | null) => void {
  const id = useId();
  const yieldRef = useRef(yieldFn);
  useEffect(() => { yieldRef.current = yieldFn; }, [yieldFn]);
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => () => { cleanupRef.current?.(); cleanupRef.current = null; }, []);

  return useCallback((el: HTMLVideoElement | null) => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    if (!el) return;
    const unregister = register(id, () => yieldRef.current());
    const isAudible = () => !el.muted && el.volume > 0 && !el.paused;
    const onPlay = () => { if (isAudible()) requestFocus(id); };
    const onVolume = () => { if (isAudible()) requestFocus(id); };
    const onStop = () => releaseFocus(id);
    el.addEventListener('play', onPlay);
    el.addEventListener('volumechange', onVolume);
    el.addEventListener('pause', onStop);
    el.addEventListener('ended', onStop);
    cleanupRef.current = () => {
      el.removeEventListener('play', onPlay);
      el.removeEventListener('volumechange', onVolume);
      el.removeEventListener('pause', onStop);
      el.removeEventListener('ended', onStop);
      unregister();
    };
  }, [id]);
}
